"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useCustomerProductLists = useCustomerProductLists;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _asyncToGenerator2 = _interopRequireDefault(require("@babel/runtime/helpers/asyncToGenerator"));
var _react = require("react");
var _contexts = require("../contexts");
var _utils = require("../utils");
var _useCustomer = require("./useCustomer");
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; } /*
 * Copyright (c) 2022, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
/**
 * This hook is designed to add customer product list capabilities
 * to your app, it leverages the Commerce API - Shopper Customer endpoints.
 * It uses a React context to store customer product list globally.
 *
 * By default, the Shopper Customer Product List API allows a shopper to
 * save multiple product lists. However, the PWA only use a single
 * product list, which is the wishlist. There is another hook useWishlist,
 * which is built on top of this hook to add wishlist specific logic.
 *
 * This Hook only works when your components are wrapped in CustomerProductListProvider.
 */
function useCustomerProductLists() {
  const api = (0, _contexts.useCommerceAPI)();
  const customer = (0, _useCustomer.useCustomer)();
  const {
    state,
    actions
  } = (0, _react.useContext)(_contexts.CustomerProductListsContext);
  const _getLists = (0, _utils.handleAsyncError)(() => {
    // @ts-ignore
    return api.shopperCustomers.getCustomerProductLists({
      parameters: {
        customerId: customer.customerId
      }
    });
  });
  const _createList = (0, _utils.handleAsyncError)((name, type) => {
    // @ts-ignore
    return api.shopperCustomers.createCustomerProductList({
      body: {
        type,
        name
      },
      parameters: {
        customerId: customer.customerId
      }
    });
  });
  const _getList = (0, _utils.handleAsyncError)(listId => {
    // @ts-ignore
    return api.shopperCustomers.getCustomerProductList({
      parameters: {
        customerId: customer.customerId,
        listId
      }
    });
  });
  const _createListItem = (0, _utils.handleAsyncError)((listId, item) => {
    const {
      id,
      quantity
    } = item;
    // @ts-ignore
    return api.shopperCustomers.createCustomerProductListItem({
      body: {
        productId: id,
        quantity,
        public: false,
        priority: 1,
        type: 'product'
      },
      parameters: {
        customerId: customer.customerId,
        listId
      }
    });
  });
  const _updateListItem = (0, _utils.handleAsyncError)((listId, item) => {
    const {
      id,
      quantity
    } = item;
    // @ts-ignore
    return api.shopperCustomers.updateCustomerProductListItem({
      body: {
        id,
        quantity,
        public: false,
        priority: 1
      },
      parameters: {
        customerId: customer.customerId,
        listId: listId,
        itemId: item.id
      }
    });
  });
  const _removeListItem = (0, _utils.handleAsyncError)((listId, itemId) => {
    // @ts-ignore
    return api.shopperCustomers.deleteCustomerProductListItem({
      parameters: {
        itemId,
        listId,
        customerId: customer.customerId
      }
    }, true);
  });
  const self = (0, _react.useMemo)(() => {
    return {
      data: state.productLists,
      get isInitialized() {
        return state.productLists !== undefined;
      },
      reset() {
        actions.reset();
      },
      /**
       * Get customer's product lists.
       */
      getLists: function () {
        var _ref = (0, _asyncToGenerator2.default)(function* () {
          const lists = yield _getLists();
          actions.receiveLists(lists);
          return lists;
        });
        return function getLists() {
          return _ref.apply(this, arguments);
        };
      }(),
      /**
       * Create a new product list.
       * @param {string} name
       * @param {string} type
       */
      createList: function () {
        var _ref2 = (0, _asyncToGenerator2.default)(function* (name, type) {
          const list = yield _createList(name, type);
          actions.receiveList(list);
          return list;
        });
        return function createList(_x, _x2) {
          return _ref2.apply(this, arguments);
        };
      }(),
      /**
       * Get a specific product list by id.
       * @param {string} listId
       * @param {object} options
       */
      getList: function () {
        var _ref3 = (0, _asyncToGenerator2.default)(function* (listId, options) {
          const {
            detail
          } = options || {};
          let list = yield _getList(listId);
          if (detail) {
            // automatically fetch details of the items in the list
            list = yield self.getProductDetails(list);
          }
          actions.receiveList(list);
          return list;
        });
        return function getList(_x3, _x4) {
          return _ref3.apply(this, arguments);
        };
      }(),
      /**
       * Get a product list for the registered user or
       * creates a new list if none exists, due to the api
       * limitation, we can not filter the lists based on
       * name/type, therefore it fetches all lists.
       * @param {string} name
       * @param {string} type
       * @param {object} options
       * @param {boolean} options.detail boolean flag to enable/disable fetching product details
       */
      getOrCreateList: function () {
        var _ref4 = (0, _asyncToGenerator2.default)(function* (name, type, options) {
          var _response$data;
          const {
            detail
          } = options || {};
          let response = yield _getLists();

          // Note: if list is empty, the API response
          // does NOT contain the "data" key.
          let list = (_response$data = response.data) === null || _response$data === void 0 ? void 0 : _response$data.find(list => list.name === name);
          if (!list) {
            list = yield _createList(name, type);
          }
          if (list && detail) {
            list = yield self.getProductDetails(list);
          }
          actions.receiveList(list);
          return list;
        });
        return function getOrCreateList(_x5, _x6, _x7) {
          return _ref4.apply(this, arguments);
        };
      }(),
      /**
       * Adds an item to the customer's product list.
       * @param {string} listId
       * @param {Object} item item to be added to the list.
       */
      createListItem: function () {
        var _ref5 = (0, _asyncToGenerator2.default)(function* (listId, item) {
          const createdItem = yield _createListItem(listId, item);
          actions.createListItem(listId, createdItem);
          return createdItem;
        });
        return function createListItem(_x8, _x9) {
          return _ref5.apply(this, arguments);
        };
      }(),
      /**
       * Update an item in a customer product list
       *
       * @param {string} listId id of the list to update the item in
       * @param {object} item
       * @param {string} item.id the id of the item in the product list
       * @param {number} item.quantity the quantity of the item
       */
      updateListItem: function () {
        var _ref6 = (0, _asyncToGenerator2.default)(function* (listId, item) {
          const {
            id,
            quantity
          } = item;
          if (quantity === 0) {
            yield _removeListItem(listId, id);
            actions.removeListItem(listId, id);
            return;
          }
          const updatedItem = yield _updateListItem(listId, item);
          actions.updateListItem(listId, updatedItem);
          return updatedItem;
        });
        return function updateListItem(_x10, _x11) {
          return _ref6.apply(this, arguments);
        };
      }(),
      /**
       * Remove an item from a customer product list
       *
       * @param {string} listId id of the list to update the item in
       * @param {string} itemId the id of the item in the product list
       */
      removeListItem: function () {
        var _ref7 = (0, _asyncToGenerator2.default)(function* (listId, itemId) {
          yield _removeListItem(listId, itemId);
          actions.removeListItem(listId, itemId);
        });
        return function removeListItem(_x12, _x13) {
          return _ref7.apply(this, arguments);
        };
      }(),
      /**
       * Remove an item from a customer product list
       *
       * @param {string} listId id of the list to update the item in
       * @param {string} productId the id of the product
       */
      removeListItemByProductId(listId, productId) {
        const item = self.findItemByProductId(listId, productId);
        if (!item) {
          console.warn(`Cannot remove item because product ${productId} is not in the list.`);
          return;
        }
        return self.removeListItem(listId, item.id);
      },
      /**
       * Get all item details for a product list.
       * @param {object} list product list
       * @param {array} list.customerProductListItems items array
       * @returns {Object} customer product list
       */
      getProductDetails: (0, _utils.handleAsyncError)( /*#__PURE__*/function () {
        var _ref8 = (0, _asyncToGenerator2.default)(function* (list) {
          // Warning, there is a weird API behavior
          // where if the product list is newly created
          // the "customerProductListItems" key will be missing
          // from the response, so we need to guard it.
          if (!list.customerProductListItems) {
            return _objectSpread(_objectSpread({}, list), {}, {
              hasDetail: true
            });
          }
          const ids = list.customerProductListItems.map(item => item.productId);
          // @ts-ignore
          const productDetails = yield api.shopperProducts.getProducts({
            parameters: {
              ids: ids.join(','),
              allImages: true
            }
          });

          // `getProducts` endpoint does not include `setProducts` data
          // so for each product set, we'll fetch it and append it to existing data
          const productSetFetches = productDetails.data.filter(product => product.type.set).map(product => {
            // @ts-ignore
            return api.shopperProducts.getProduct({
              parameters: {
                id: product.id
              }
            }).then(data => {
              product.setProducts = data.setProducts;
            });
          });
          yield Promise.all(productSetFetches);
          const result = self.mergeProductDetailsIntoList(list, productDetails);

          // hasDetail is a flag to indicate
          // the list items are populated
          // with product detail data
          result.hasDetail = true;
          return _objectSpread(_objectSpread({}, result), {}, {
            hasDetail: true
          });
        });
        return function (_x14) {
          return _ref8.apply(this, arguments);
        };
      }()),
      /**
       * The customer product list API does NOT return the
       * product item details, this utility function is used
       * when you fetch the item details manually and insert
       * into the lists.
       * This function is often used with getProductDetails.
       */
      mergeProductDetailsIntoList(list, productDetails) {
        var _list$customerProduct;
        const items = (_list$customerProduct = list.customerProductListItems) === null || _list$customerProduct === void 0 ? void 0 : _list$customerProduct.map(item => {
          const product = _objectSpread({}, productDetails.data.find(product => product.id === item.productId));
          return _objectSpread(_objectSpread({}, item), {}, {
            product
          });
        });
        return _objectSpread(_objectSpread({}, list), {}, {
          customerProductListItems: items
        });
      },
      /**
       * Find the product list by its name.
       * @param {string} name
       * @returns {object} customer product list
       */
      findListByName(name) {
        if (!self.data) {
          return undefined;
        }
        return Object.values(self.data).find(list => list.name === name);
      },
      /**
       * Find the product list by its id.
       * @param {string} id
       * @returns {object} customer product list
       */
      findListById(id) {
        if (!self.data) {
          return undefined;
        }
        return Object.values(self.data).find(list => list.id === id);
      },
      /**
       * Find the item from list.
       * @param {string} listId
       * @param {string} productId
       * @returns {object} product list item
       */
      findItemByProductId(listId, productId) {
        var _self$findListById, _self$findListById$cu;
        return (_self$findListById = self.findListById(listId)
        // @ts-ignore
        ) === null || _self$findListById === void 0 ? void 0 : (_self$findListById$cu = _self$findListById.customerProductListItems) === null || _self$findListById$cu === void 0 ? void 0 : _self$findListById$cu.find(item => item.productId === productId);
      }
    };
  }, [customer.customerId, state]);
  return self;
}